/**
 * TNK-43 — pokemoncard.io deck search + import for the web operator.
 *
 * Port of the mobile artifact at
 * `/data/TCG-Store/Tnk-tcg-app/artifacts/mobile/app/tools/deck-search.tsx`.
 * Two-state screen: search-and-paste-URL on entry, deck preview after
 * a selection. Selecting a deck loads its card list via the server
 * proxy (rate-limited 30/min per IP); the operator picks an optional
 * source lot and imports the cards into inventory via the existing
 * AppContext.addCards path (each card lands as Unlisted, NM, qty from
 * the deck list, cost 0).
 *
 * Manager+ — the server endpoints are gated the same way.
 */
import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  ChevronRight,
  Layers,
  Link as LinkIcon,
  Search,
  X,
} from "lucide-react";

import { BackButton } from "@/components/BackButton";
import { ManagerOnly } from "@/components/ManagerOnly";
import { PrimaryButton } from "@/components/PrimaryButton";
import { generateId, useApp, type Card } from "@/context/AppContext";
import {
  fetchDeckCards,
  searchDecks,
  type DeckCard,
  type DeckSearchResult,
} from "@/features/pokemoncardio";

export const Route = createFileRoute("/tools/deck-search")({
  component: () => (
    <ManagerOnly>
      <DeckSearchPage />
    </ManagerOnly>
  ),
});

type Screen = "search" | "deck";

function DeckSearchPage() {
  const { lots, addCards } = useApp();

  const [screen, setScreen] = useState<Screen>("search");

  // Search state
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<DeckSearchResult[]>([]);
  const [searchError, setSearchError] = useState("");

  // URL fetch state
  const [urlInput, setUrlInput] = useState("");

  // Deck detail state
  const [loadingDeck, setLoadingDeck] = useState(false);
  const [deckError, setDeckError] = useState("");
  const [deckTitle, setDeckTitle] = useState("");
  const [deckCards, setDeckCards] = useState<DeckCard[]>([]);
  const [deckUrl, setDeckUrl] = useState("");
  const [activeDeckMeta, setActiveDeckMeta] = useState<{
    format?: string;
    price?: string;
    author?: string;
    views?: string;
  } | null>(null);

  // Import state
  const [sourceLotId, setSourceLotId] = useState("");
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ cards: number } | null>(
    null,
  );

  const totalCards = deckCards.reduce((s, c) => s + c.quantity, 0);

  const handleSearch = async () => {
    const q = query.trim();
    if (!q) return;
    setSearchError("");
    setSearchResults([]);
    setSearching(true);
    try {
      const results = await searchDecks(q);
      if (results.length === 0) {
        setSearchError("No decks found. Try a different search term.");
      } else {
        setSearchResults(results);
      }
    } catch (e) {
      setSearchError(
        e instanceof Error
          ? `${e.message} You can still paste a deck URL below.`
          : "Search failed. You can still paste a deck URL below.",
      );
    } finally {
      setSearching(false);
    }
  };

  const loadDeck = async (
    url: string,
    title: string,
    meta?: DeckSearchResult,
  ) => {
    setDeckError("");
    setDeckCards([]);
    setImportResult(null);
    setLoadingDeck(true);
    setScreen("deck");
    setDeckUrl(url);
    setDeckTitle(title);
    setActiveDeckMeta(
      meta
        ? {
            format: meta.format,
            price: meta.price,
            author: meta.author,
            views: meta.views,
          }
        : null,
    );
    try {
      const cards = await fetchDeckCards(url);
      setDeckCards(cards);
      if (cards.length === 0) {
        setDeckError("Deck list parsed but contained no valid card lines.");
      }
    } catch (e) {
      setDeckError(
        e instanceof Error
          ? `Could not load deck: ${e.message}`
          : "Could not load deck.",
      );
    } finally {
      setLoadingDeck(false);
    }
  };

  const handleFetchUrl = () => {
    const url = urlInput.trim();
    if (!url) return;
    const slug = url.replace(/.*\/deck\//, "").replace(/\/+$/, "");
    const title = slug
      .replace(/-/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
    void loadDeck(url, title);
  };

  const handleImport = () => {
    if (deckCards.length === 0) return;
    setImporting(true);
    setImportResult(null);
    const now = new Date().toISOString();
    const cards: Card[] = deckCards.map((dc) => ({
      id: generateId("SKU"),
      name: dc.name,
      game: "Pokemon",
      setName: "",
      setCode: dc.setCode,
      cardNumber: dc.cardNumber,
      rarity: "Common",
      variant: "",
      condition: "NM",
      quantity: dc.quantity,
      sourceLotId,
      costBasis: 0,
      marketPrice: 0,
      expectedSalePrice: 0,
      storageLocation: "",
      listingStatus: "Unlisted",
      saleVenue: "",
      notes: `Imported from PokemonCard.io: ${deckTitle}`,
      createdAt: now,
    }));
    void addCards(cards).then(() => {
      setImporting(false);
      setImportResult({ cards: cards.length });
    });
  };

  const goBackToSearch = () => {
    setScreen("search");
    setDeckCards([]);
    setDeckError("");
    setImportResult(null);
    setActiveDeckMeta(null);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <section className="space-y-4 pb-12">
      <div className="flex items-center justify-between gap-2 pb-3">
        <BackButton fallback={{ to: "/tools" }} label="Tools" />
      </div>

      {screen === "search" ? (
        <>
          <div className="border-b border-border pb-3">
            <h1 className="text-2xl font-bold text-foreground">Deck Search</h1>
            <p className="text-sm text-muted-foreground">
              Search PokemonCard.io for competitive decks. Select a result to
              preview and import its card list, or paste a deck URL directly.
            </p>
          </div>

          {/* Search bar */}
          <div className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void handleSearch();
                }
              }}
              placeholder="Search decks… e.g. Charizard, Gardevoir"
              autoComplete="off"
              autoCorrect="off"
              className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
            />
            {query.length > 0 && (
              <button
                type="button"
                onClick={() => setQuery("")}
                aria-label="Clear search"
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <PrimaryButton
            label={searching ? "Searching…" : "Search"}
            onClick={handleSearch}
            loading={searching}
            disabled={!query.trim()}
          />

          {searchError && (
            <div className="flex items-start gap-2 rounded-md bg-destructive/10 p-2.5 text-xs text-destructive">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{searchError}</span>
            </div>
          )}

          {searchResults.length > 0 && (
            <div className="overflow-hidden rounded-md border border-border bg-card">
              <div className="px-3 py-2 text-[10px] font-medium tracking-wider text-muted-foreground">
                {searchResults.length} RESULT
                {searchResults.length !== 1 ? "S" : ""}
              </div>
              {searchResults.map((result, i) => (
                <button
                  key={result.slug}
                  type="button"
                  onClick={() =>
                    void loadDeck(result.url, result.title, result)
                  }
                  className={`flex w-full items-center gap-2 px-3 py-3 text-left hover:bg-card/80 ${
                    i > 0 ? "border-t border-border" : ""
                  }`}
                >
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="text-sm font-semibold text-foreground">
                      {result.title}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {result.format && (
                        <span
                          className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                            result.format.toLowerCase() === "standard"
                              ? "bg-primary/15 text-primary"
                              : "bg-secondary/15 text-secondary-foreground"
                          }`}
                        >
                          {result.format}
                        </span>
                      )}
                      {result.price && (
                        <span className="text-[11px] text-muted-foreground">
                          {result.price}
                        </span>
                      )}
                      {result.author && (
                        <span className="text-[11px] text-muted-foreground">
                          by {result.author}
                        </span>
                      )}
                      {result.views && (
                        <span className="text-[11px] text-muted-foreground">
                          {result.views}
                        </span>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                </button>
              ))}
            </div>
          )}

          {/* Divider */}
          <div className="relative py-2">
            <div className="absolute inset-0 flex items-center">
              <div className="h-px w-full bg-border" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-background px-3 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                or paste a deck URL
              </span>
            </div>
          </div>

          {/* URL paste */}
          <div className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2">
            <LinkIcon className="h-4 w-4 text-muted-foreground" />
            <input
              type="url"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleFetchUrl();
                }
              }}
              placeholder="https://pokemoncard.io/deck/…"
              autoCapitalize="none"
              autoCorrect="off"
              className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
            />
          </div>

          <PrimaryButton
            label="Fetch Deck"
            onClick={handleFetchUrl}
            variant="secondary"
            disabled={!urlInput.trim()}
          />
        </>
      ) : (
        <>
          <button
            type="button"
            onClick={goBackToSearch}
            className="-ml-1 inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Search Results
          </button>

          <h1 className="text-2xl font-bold text-foreground">
            {deckTitle || "Deck Preview"}
          </h1>

          {activeDeckMeta && (
            <div className="flex flex-wrap items-center gap-2">
              {activeDeckMeta.format && (
                <span
                  className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                    activeDeckMeta.format.toLowerCase() === "standard"
                      ? "bg-primary/15 text-primary"
                      : "bg-secondary/15 text-secondary-foreground"
                  }`}
                >
                  {activeDeckMeta.format}
                </span>
              )}
              {activeDeckMeta.price && (
                <span className="text-[11px] text-muted-foreground">
                  {activeDeckMeta.price}
                </span>
              )}
              {activeDeckMeta.author && (
                <span className="text-[11px] text-muted-foreground">
                  by {activeDeckMeta.author}
                </span>
              )}
              {activeDeckMeta.views && (
                <span className="text-[11px] text-muted-foreground">
                  {activeDeckMeta.views}
                </span>
              )}
            </div>
          )}

          {deckUrl && (
            <p className="truncate text-xs text-muted-foreground">
              {deckUrl.replace("https://", "")}
            </p>
          )}

          {loadingDeck && (
            <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              Loading deck list…
            </div>
          )}

          {deckError && (
            <div className="flex items-start gap-2 rounded-md bg-destructive/10 p-2.5 text-xs text-destructive">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{deckError}</span>
            </div>
          )}

          {deckCards.length > 0 && !loadingDeck && (
            <>
              <div className="flex items-center gap-2 rounded-md bg-primary/10 px-3 py-2 text-xs font-semibold text-primary">
                <Layers className="h-3.5 w-3.5" />
                <span>
                  {deckCards.length} unique card
                  {deckCards.length !== 1 ? "s" : ""} · {totalCards} total
                </span>
              </div>

              {lots.length > 0 && (
                <div className="space-y-1.5">
                  <div className="text-[10px] font-medium tracking-wider text-muted-foreground">
                    ASSIGN TO LOT (OPTIONAL)
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {lots.map((l) => {
                      const selected = sourceLotId === l.id;
                      const label = l.seller
                        ? `${l.seller} · ${l.purchaseDate}`
                        : l.purchaseDate || l.id;
                      return (
                        <button
                          key={l.id}
                          type="button"
                          onClick={() => setSourceLotId(selected ? "" : l.id)}
                          className={`max-w-[260px] truncate rounded-md border px-2.5 py-1.5 text-xs ${
                            selected
                              ? "border-secondary bg-secondary/15 font-semibold text-secondary-foreground"
                              : "border-border bg-card text-muted-foreground hover:bg-card/80"
                          }`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <PrimaryButton
                label={
                  importing
                    ? "Importing…"
                    : `Import ${totalCards} Card${totalCards !== 1 ? "s" : ""}`
                }
                onClick={handleImport}
                loading={importing}
              />

              {importResult && (
                <div className="flex items-center gap-2 rounded-md bg-success/15 px-3 py-2 text-xs font-semibold text-success">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  {importResult.cards} card
                  {importResult.cards !== 1 ? "s" : ""} imported to inventory
                </div>
              )}

              <div className="overflow-hidden rounded-md border border-border bg-card">
                <div className="px-3 py-2 text-[10px] font-medium tracking-wider text-muted-foreground">
                  CARD LIST
                </div>
                {deckCards.map((c, i) => (
                  <div
                    key={`${c.setCode}-${c.cardNumber}-${i}`}
                    className={`flex items-center gap-3 px-3 py-2.5 ${
                      i > 0 ? "border-t border-border" : ""
                    }`}
                  >
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-bold text-primary">
                      {c.quantity}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-foreground">
                        {c.name}
                      </div>
                      <div className="text-[11px] text-muted-foreground">
                        {c.setCode}-{c.cardNumber}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
}
